import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from 'services/authService';
import * as S from './ResetPassword.styled';

interface LocationState {
  email: string;
  verificationToken: string;
}

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isResetting, setIsResetting] = useState<boolean>(false);

  // state가 없으면 searchmain으로 리다이렉트
  useEffect(() => {
    if (!state || !state.email || !state.verificationToken) {
      navigate('/searchmain', { replace: true });
    }
  }, [state, navigate]);

  if (!state || !state.email || !state.verificationToken) {
    return null;
  }

  const validatePassword = (pwd: string): boolean => {
    // 영문과 숫자를 포함한 7~15자리
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d!@#$%&*]{7,15}$/;
    return passwordRegex.test(pwd);
  };

  const handleResetPassword = async () => {
    setErrorMessage('');

    if (!password) {
      setErrorMessage('비밀번호를 입력해주세요.');
      return;
    }

    if (!validatePassword(password)) {
      setErrorMessage('영문과 숫자를 조합하여 7~15자의 비밀번호를 입력해주세요.');
      return;
    }

    if (!passwordConfirm) {
      setErrorMessage('비밀번호 확인을 입력해주세요.');
      return;
    }

    if (password !== passwordConfirm) {
      setErrorMessage('비밀번호가 일치하지 않습니다.');
      return;
    }

    setIsResetting(true);

    try {
      await authService.resetPassword({
        email: state.email,
        password: password,
        verificationToken: state.verificationToken
      });

      navigate('/reset-password-complete');
    } catch (error) {
      console.error('비밀번호 재설정 실패:', error);
      setErrorMessage('비밀번호 재설정에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsResetting(false);
    }
  };

  const isFormValid = password && passwordConfirm && password === passwordConfirm && validatePassword(password);

  return (
    <S.Page>
      <S.Header>아이디/비밀번호 찾기</S.Header>

      <S.TabContainer>
        <S.Tab active={false}>아이디 찾기</S.Tab>
        <S.Tab active={true}>비밀번호 찾기</S.Tab>
      </S.TabContainer>

      <S.ContentWrap>
        <S.Message>새로운 비밀번호를 입력해주세요.</S.Message>

        <div>
          <S.SectionTitle>비밀번호</S.SectionTitle>

          <S.InputWrap>
            <input
              type="password"
              placeholder="영문, 숫자 포함 7~15자리"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </S.InputWrap>

          <S.InputWrap>
            <input
              type="password"
              placeholder="비밀번호를 다시 입력해주세요."
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
            />
          </S.InputWrap>

          {passwordConfirm && password !== passwordConfirm && (
            <S.ErrorMessage>비밀번호가 일치하지 않습니다.</S.ErrorMessage>
          )}
          {password && !validatePassword(password) && (
            <S.HelperText>
              영문과 숫자를 조합하여 7~15자의 비밀번호를 입력해주세요.<br />
              특수문자(!, @, #, $, %, &, *) 가능
            </S.HelperText>
          )}
          {errorMessage && <S.ErrorMessage>{errorMessage}</S.ErrorMessage>}
        </div>
      </S.ContentWrap>

      <S.BottomButton
        className={isFormValid ? 'active' : ''}
        onClick={handleResetPassword}
        disabled={!isFormValid || isResetting}
      >
        {isResetting ? '처리 중...' : '확인'}
      </S.BottomButton>
    </S.Page>
  );
}

export default ResetPassword;
